<template>
  <label
    :id="id"
    class="s-checkbox"
    :class="[
      { 'is-disabled': isDisabled },
      { 'is-readonly': isReadonly },
      { 'is-bordered': border },
      { 'is-checked': isChecked }
    ]">
    <span
      :aria-checked="indeterminate ? 'mixed' : false"
      class="s-checkbox__input"
      :class="{
        'is-disabled': isDisabled,
        'is-readonly': isReadonly,
        'is-checked': isChecked,
        'is-indeterminate': indeterminate,
        'is-focus': focus}"
      :role="indeterminate ? 'checkbox' : false"
      :tabindex="indeterminate ? 0 : false">
      <span class="s-checkbox__inner"/>
      <input
        v-if="trueLabel || falseLabel"
        v-model="model"
        :aria-hidden="indeterminate ? 'true' : 'false'"
        class="s-checkbox__original"
        data-qa="checkbox"
        :disabled="isDisabled || isReadonly"
        :false-value="falseLabel"
        :name="name"
        :state="isChecked ? 'check' : 'uncheck'"
        :true-value="trueLabel"
        type="checkbox"
        @blur="focus = false"
        @change="handleChange"
        @focus="focus = true">
      <input
        v-else
        v-model="model"
        :aria-hidden="indeterminate ? 'true' : 'false'"
        class="s-checkbox__original"
        data-qa="checkbox"
        :disabled="isDisabled || isReadonly"
        :name="name"
        :state="isChecked ? 'check' : 'uncheck'"
        type="checkbox"
        :value="label"
        @blur="focus = false"
        @change="handleChange"
        @focus="focus = true">
    </span>
    <span
      v-if="$slots.default || label"
      class="s-checkbox__label"
      data-qa="checkbox-label">
      <!-- @slot Используется для вставки текста, описания и т.п. -->
      <slot/>
      <template v-if="!$slots.default">{{ label }}</template>
    </span>
  </label>
</template>
<script>
import Form from '../../mixins/form.js'
import emitter from '../../mixins/emitter'

/**
 * Обычный Checkbox
 * @displayName Checkbox
 */
export default {
  name: 'sCheckbox',
  mixins: [emitter, Form],
  componentName: 'sCheckbox',
  data() {
    return {
      selfModel: false,
      focus: false,
      isLimitExceeded: false
    }
  },
  computed: {
    model: {
      get() {
        return this.isGroup
          ? this.store : this.value !== undefined
            ? this.value : this.selfModel
      },
      set(val) {
        if (this.isGroup) {
          this.isLimitExceeded = false;
          (this.isGroup.min !== undefined &&
              val.length < this.isGroup.min &&
              (this.isLimitExceeded = true));
          (this.isGroup.max !== undefined &&
              val.length > this.isGroup.max &&
              (this.isLimitExceeded = true))
          this.isLimitExceeded === false &&
            this.dispatch('sCheckboxGroup', 'input', [val])
        } else {
        /**
         * Срабатывает только для одиночного чекбокса. Для групп не срабатывает
         * @event input
         * @type {Event}
         */
          this.$emit('input', val)
          this.selfModel = val
        }
      }
    },
    isChecked() {
      if ({}.toString.call(this.model) === '[object Boolean]') {
        return this.model
      } else if (Array.isArray(this.model)) {
        return this.model.indexOf(this.label) > -1
      } else if (this.model !== null && this.model !== undefined) {
        return this.model === this.trueLabel
      }
      return false
    },
    isGroup() {
      let parent = this.$parent
      while (parent) {
        if (parent.$options.componentName !== 'sCheckboxGroup') {
          parent = parent.$parent
        } else {
          return parent
        }
      }
      return false
    },
    store() {
      return this.isGroup ? this.isGroup.value : this.value
    },
    isLimitDisabled() {
      const { max, min } = this.isGroup
      return !!(max || min) &&
          (this.model?.length >= max && !this.isChecked) ||
          (this.model?.length <= min && this.isChecked)
    },
    isDisabled() {
      return this.isGroup
        ? this.isGroup.disabled || this.disabled || (this.sForm || {}).disabled || this.isLimitDisabled
        : this.disabled || (this.sForm || {}).disabled
    },
    isReadonly() {
      return this.isGroup
        ? this.isGroup.readonly || this.readonly || (this.sForm || {}).readonly
        : this.readonly || (this.sForm || {}).readonly
    },
  },
  props: {
    /**
     * Значение
     */
    value: {
      type: [String, Number, Boolean],
    },
    /**
     * значение при использовании внутри группы
     */
    label: {
      type: [String, Number, Boolean],
    },
    /**
     * использовать неопределенное состояние
     */
    indeterminate: Boolean,
    /**
     * отключение
     */
    disabled: {
      type: Boolean,
    },
    /**
     * Только чтение
     */
    readonly: {
      type: Boolean,
    },
    checked: {
      type: Boolean,
    },
    /**
     * @ignore
     * собственный атрибут 'name'
     */
    name: {
      type: String,
    },
    /**
     * значение, если отмечено
     */
    trueLabel: {
      type: [String, Number],
    },
    /**
     * значение, если не отмечено
     */
    falseLabel: {
      type: [String, Number],
    },
    /**
     * @ignore
     */
    id: {
      type: String,
    },
    /**
     * проброс значения а aria-controls
     */
    controls: {
      type: String,
    },
    /**
     * нужно ли добавлять рамку
     */
    border: {
      type: Boolean,
    },
  },
  methods: {
    addToStore() {
      if (
        Array.isArray(this.model) &&
          this.model.indexOf(this.label) === -1
      ) {
        this.model.push(this.label)
      } else {
        this.model = this.trueLabel || true
      }
    },
    handleChange(ev) {
      if (this.isLimitExceeded) { return }
      let value
      if (ev.target.checked) {
        value = this.trueLabel === undefined ? true : this.trueLabel
      } else {
        value = this.falseLabel === undefined ? false : this.falseLabel
      }
      /**
     * Изменение значения
     * @event change
     * @type {Event}
     */
      this.$emit('change', value, ev)
      this.$nextTick(() => {
        if (this.isGroup) {
          this.dispatch('sCheckboxGroup', 'change', [this.isGroup.value])
        }
      })
    }
  },
  created() {
    this.checked && this.addToStore()
  },
  mounted() {
    if (this.indeterminate) {
      this.$el.setAttribute('aria-controls', this.controls)
    }
  },
  watch: {
    value(value) {
      this.dispatch('sFormItem', 's.form.change', value)
    }
  }
}
</script>
